/**
 * Acquisti dentro l'app, su iOS e Android, tramite RevenueCat (#35 #36 #38).
 *
 * Una implementazione sola per due piattaforme: è il motivo per cui si è
 * scelto RevenueCat (#32). L'SDK parla con StoreKit e con Play Billing, e
 * quello che torna qui ha la stessa forma da entrambe le parti.
 *
 * Il Pro NON si decide qui: dopo l'acquisto RevenueCat chiama il webhook
 * (#37), che lo scrive in Firestore dalla porta unica (#33), e l'app lo vede
 * dal profilo. Qui si compra, si ripristina e si apre la gestione.
 *
 * Le chiavi pubbliche dell'SDK arrivano dalla build:
 *   REACT_APP_REVENUECAT_IOS_KEY      (appl_…)
 *   REACT_APP_REVENUECAT_ANDROID_KEY  (goog_…)
 *   REACT_APP_REVENUECAT_TEST_KEY     (test_…, Test Store: SOLO build di
 *                                      sviluppo, nelle build di release
 *                                      l'SDK chiude l'app)
 * Senza chiave `getProducts()` torna una lista vuota: per il paywall vuol
 * dire «qui non si compra», niente prezzi e nessun pulsante.
 */

import { Capacitor } from "@capacitor/core";
import logger from "../logger";
import { withExternalFlow } from "../../features/themes/externalFlow";

const PIANI = { MONTHLY: "monthly", ANNUAL: "yearly" };

function chiave() {
  const piattaforma = Capacitor.getPlatform();
  if (process.env.REACT_APP_REVENUECAT_TEST_KEY) return process.env.REACT_APP_REVENUECAT_TEST_KEY;
  if (piattaforma === "ios") return process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
  if (piattaforma === "android") return process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
  return "";
}

let sdkPronto = null;
// A che punto è il caricamento: il paywall la scrive nel log se resta appeso (21/10).
let fase = "ferma";

// Avvisi ed errori di RevenueCat finiscono nei nostri log, perché è lì che si
// vede come mai l'App Store non dà i prodotti. Degli avvisi solo quelli che
// parlano di prodotti e offerte: i report sono al massimo 20 per sessione.
function logDiRevenueCat(livello, messaggio) {
  const testo = String(messaggio || "");
  if (livello === "ERROR" || (livello === "WARN" && /product|storekit|offering|configur/i.test(testo))) {
    logger.error(`RevenueCat ${livello}`, { error: testo.slice(0, 400) });
  }
}

// L'SDK si configura una volta sola, alla prima richiesta. Se la
// configurazione fallisce si riprova alla richiesta dopo.
function sdk() {
  if (!sdkPronto) {
    sdkPronto = (async () => {
      fase = "chiave";
      const apiKey = chiave();
      if (!apiKey || !Capacitor.isNativePlatform()) return null;
      fase = "import";
      const { Purchases } = await import("@revenuecat/purchases-capacitor");
      fase = "log";
      try {
        await Purchases.setLogHandler(logDiRevenueCat);
      } catch (e) {
        // I log di RevenueCat aiutano, ma non devono mai fermare gli acquisti.
      }
      fase = "configure";
      await Purchases.configure({ apiKey });
      fase = "configurato";
      // Dentro un oggetto, mai da solo: il plugin Capacitor è un Proxy che ha anche
      // `then`, quindi una promise che lo restituisce non si risolve mai (21/10).
      return { Purchases };
    })().catch((err) => {
      logger.error("billing: configurazione di RevenueCat non riuscita", { error: err.message });
      sdkPronto = null;
      return null;
    });
  }
  return sdkPronto;
}

// Solo una prova gratuita è una prova: uno sconto d'ingresso a pagamento no.
function prova(introPrice) {
  if (!introPrice || introPrice.price !== 0 || !introPrice.periodNumberOfUnits) return null;
  return { count: introPrice.periodNumberOfUnits, unit: String(introPrice.periodUnit || "").toLowerCase() };
}

let pacchetti = new Map();

const PAGINE_ABBONAMENTI = {
  appstore: "https://apps.apple.com/account/subscriptions",
  play: "https://play.google.com/store/account/subscriptions?package=com.giadalabs.puzzly",
};

export const nativeBilling = {
  name: "revenuecat",

  get fase() {
    return fase;
  },

  async getProducts() {
    const { Purchases } = (await sdk()) || {};
    if (!Purchases) {
      logger.error("billing: acquisti in-app non configurati su questa build");
      return [];
    }
    fase = "offerte";
    const offerte = await Purchases.getOfferings();
    fase = "offerte arrivate";
    const lista = (offerte && offerte.current && offerte.current.availablePackages) || [];
    if (lista.length === 0) {
      logger.error("billing: nessun pacchetto da comprare", {
        error: `offerta corrente: ${(offerte && offerte.current && offerte.current.identifier) || "nessuna"}, offerte: ${Object.keys((offerte && offerte.all) || {}).length}`,
      });
    }
    pacchetti = new Map();
    return lista
      .filter((p) => PIANI[p.packageType])
      .map((p) => {
        pacchetti.set(p.identifier, p);
        const plan = PIANI[p.packageType];
        return {
          id: p.identifier,
          plan,
          price: p.product.priceString,
          period: plan === "yearly" ? "year" : "month",
          hasMonthlyEquiv: plan === "yearly" && !!p.product.pricePerMonthString,
          perMonth: p.product.pricePerMonthString || null,
          trial: prova(p.product.introPrice),
        };
      });
  },

  // Torna true se l'acquisto è andato, false se l'utente ha chiuso il foglio.
  async purchase(productId, { uid } = {}) {
    const { Purchases } = (await sdk()) || {};
    if (!Purchases) throw new Error("Gli acquisti in-app non sono configurati su questa build.");
    const pacchetto = pacchetti.get(productId);
    if (!pacchetto) throw new Error(`Prodotto non trovato: ${productId}`);
    // L'acquisto va sull'uid Firebase: è quello che il webhook usa per scrivere il Pro.
    if (uid) await Purchases.logIn({ appUserID: uid });
    try {
      // Foglio dello store: flusso esterno, l'icona stagionale non cambia sotto (Task 11).
      await withExternalFlow(() => Purchases.purchasePackage({ aPackage: pacchetto }));
      return true;
    } catch (err) {
      if (err && err.userCancelled) return false;
      throw err;
    }
  },

  // Apple lo pretende (#38). Torna true se sull'account c'è un Pro attivo.
  async restore({ uid } = {}) {
    const { Purchases } = (await sdk()) || {};
    if (!Purchases) return false;
    if (uid) await Purchases.logIn({ appUserID: uid });
    const { customerInfo } = await withExternalFlow(() => Purchases.restorePurchases());
    return !!(customerInfo && customerInfo.entitlements && customerInfo.entitlements.active && customerInfo.entitlements.active.pro);
  },

  // La gestione dell'abbonamento sta nello store in cui è stato fatto (#113): la
  // pagina si apre da qui, senza chiedere a RevenueCat, che su un'installazione
  // nuova non conosce ancora l'utente. Capacitor apre gli indirizzi esterni fuori dall'app.
  async openManageSubscription(store) {
    const url = PAGINE_ABBONAMENTI[store];
    if (!url) return false;
    window.open(url, "_blank");
    return true;
  },
};
